import React from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { getIcon } from '../../utils/iconMap';
import '../../styles/components/Footer.css';

const Footer = () => {
  const { data, getContact, getUIText } = useData();
  const contact = getContact();
  const footerUI = getUIText('footer');
  const globalUI = getUIText('global');
  const currentYear = new Date().getFullYear();

  // Use navLinks from JSON or fallback to defaults
  const quickLinks = globalUI?.navLinks || [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/products', label: 'Products' },
    { path: '/services', label: 'Services' },
    { path: '/contact', label: 'Contact' },
  ];

  // Map platform names to icon names
  const socialIconMap = {
    facebook: 'FaFacebook',
    instagram: 'FaInstagram',
    youtube: 'FaYoutube',
    whatsappCommunity: 'FaWhatsapp',
    twitter: 'FaTwitter',
    linkedin: 'FaLinkedin',
  };

  const enabledSocials = contact?.socialMedia
    ? Object.entries(contact.socialMedia).filter(([_, info]) => info.enabled)
    : [];

  return (
    <footer className="footer">
      <div className="footer-pattern"></div>
      <div className="footer-content">
        <div className="container">
          <div className="footer-grid">
            {/* About Section */}
            <div className="footer-section">
              <div className="footer-logo">
                <span className="footer-brand">{data?.name}</span>
              </div>
              <p className="footer-description">
                {footerUI?.description || data?.description}
              </p>
              {enabledSocials.length > 0 && (
                <div className="footer-socials">
                  {enabledSocials.map(([platform, info]) => (
                    <a
                      key={platform}
                      href={info.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                      aria-label={info.label}
                    >
                      {getIcon(socialIconMap[platform])}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="footer-section">
              <h3 className="footer-title">{footerUI?.quickLinksTitle || 'Quick Links'}</h3>
              <ul className="footer-links">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="footer-link">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div className="footer-section">
              <h3 className="footer-title">{footerUI?.contactInfoTitle || 'Contact Info'}</h3>
              <ul className="footer-contact">
                {contact?.address && (
                  <li className="contact-item">
                    {getIcon('FaLocationDot', { className: 'contact-icon' })}
                    <span>
                      {contact.address.street}, {contact.address.city}<br />
                      {contact.address.state} - {contact.address.zipCode}
                      {contact.mapLink && (
                        <>
                          <br />
                          <a
                            href={contact.mapLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="footer-map-link"
                          >
                            View on Maps
                          </a>
                        </>
                      )}
                    </span>
                  </li>
                )}
                {contact?.phone && (
                  <li className="contact-item">
                    {getIcon('FaPhone', { className: 'contact-icon' })}
                    <span>
                      <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                      {contact.alternatePhone && (
                        <>
                          <br />
                          <a href={`tel:${contact.alternatePhone}`}>{contact.alternatePhone}</a>
                        </>
                      )}
                    </span>
                  </li>
                )}
                {contact?.email && (
                  <li className="contact-item">
                    {getIcon('FaEnvelope', { className: 'contact-icon' })}
                    <a href={`mailto:${contact.email}`}>{contact.email}</a>
                  </li>
                )}
                {contact?.businessHours && (
                  <li className="contact-item">
                    {getIcon('FaClock', { className: 'contact-icon' })}
                    <span>
                      Weekdays: {contact.businessHours.weekdays}<br />
                      Weekends: {contact.businessHours.weekends}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container">
          <p>© {currentYear} {data?.name}. {footerUI?.copyrightText || 'All Rights Reserved.'}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
